import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fetchPublicContentModule } from '../services/publicContentApi';
import type { PromoPopupItem } from '../types/content';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const DEFAULT_POPUP: PromoPopupItem = {
    id: 'promo-popup-main',
    title: 'Vestibular Macro Senac 2026',
    alt: 'Vestibular Macro Senac 2026',
    imageUrl: '/assets/pop/POP-UP_Processo Seletivo.jpg',
    href: 'https://faculdade.am.senac.br/graduacao',
    buttonLabel: 'Registre seu interesse',
    order: 1,
    isActive: true,
};

const STORAGE_KEY = 'visualizou_popup_vestibular_macro';

export default function PromoPopup() {
    const [open, setOpen] = useState(false);
    const [popup, setPopup] = useState<PromoPopupItem | null>(null);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        let isMounted = true;

        fetchPublicContentModule<PromoPopupItem>('promoPopup')
            .then((response) => {
                if (!isMounted) return;
                const activePopup = response.items.find((item) => item.isActive);
                setPopup(activePopup || null);
            })
            .catch(() => {
                if (isMounted) {
                    setPopup(DEFAULT_POPUP);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!popup) return undefined;

        const jaViu = sessionStorage.getItem(STORAGE_KEY);
        if (!jaViu) {
            const timer = setTimeout(() => {
                setOpen(true);
                sessionStorage.setItem(STORAGE_KEY, 'true');
            }, 1500);
            return () => clearTimeout(timer);
        }

        return undefined;
    }, [popup]);

    const handleClose = () => {
        setOpen(false);
    };

    if (!popup) return null;

    const hasLink = Boolean(popup.href);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(3px)',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: '#fff',
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: 24,
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: '#fff',
                        bgcolor: 'rgba(0,0,0,0.4)',
                        zIndex: 10,
                        '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.7)',
                        },
                    }}
                >
                    <CloseRoundedIcon />
                </IconButton>

                <Box
                    component={hasLink ? 'a' : 'div'}
                    href={hasLink ? popup.href : undefined}
                    target={hasLink ? '_blank' : undefined}
                    rel={hasLink ? 'noopener noreferrer' : undefined}
                    sx={{
                        display: 'block',
                        width: '100%',
                        maxWidth: 500,
                        lineHeight: 0,
                        textDecoration: 'none',
                    }}
                >
                    <Box
                        component="img"
                        src={popup.imageUrl}
                        alt={popup.alt || popup.title}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            objectFit: 'contain',
                        }}
                    />
                </Box>

                {hasLink && (
                    <DialogContent
                        sx={{
                            p: 2,
                            width: '100%',
                            textAlign: 'center',
                            bgcolor: '#f5f5f5',
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            href={popup.href}
                            target="_blank"
                            sx={{
                                borderRadius: 8,
                                px: 4,
                                py: 1.5,
                                fontWeight: 'bold',
                                background: 'linear-gradient(45deg, #0056b3 30%, #007bff 90%)',
                                boxShadow: '0 3px 5px 2px rgba(0, 123, 255, .3)',
                            }}
                        >
                            {popup.buttonLabel || 'Saiba mais'}
                        </Button>
                    </DialogContent>
                )}
            </Box>
        </Dialog>
    );
}
